var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { msg, localized } from '@lit/localize';
import './IconButton.js';
import '../icon/Icon.js';
/**
 * Overflow ("kebab") button — a small icon-only trigger that opens a
 * dropdown menu listing actions. Items flagged `danger` are grouped at
 * the bottom of the menu and visually separated by a divider.
 *
 * Emits `item-select` with `{ value, item }` on click. The host owns
 * the actions; this component only surfaces the choice.
 */
let KebabButton = class KebabButton extends LitElement {
    constructor() {
        super(...arguments);
        this.items = [];
        this.ariaLabel = '';
        this.align = 'right';
        this.size = 'small';
        this.variant = 'secondary';
        this.icon = 'kebab';
        this.special = false;
        this.outlined = true;
        this.dashed = false;
        this.disabled = false;
        this._open = false;
        this._onDocumentClick = (e) => {
            if (!this._open)
                return;
            const stillInside = e.composedPath().includes(this);
            if (!stillInside)
                this._open = false;
        };
        this._toggle = (e) => {
            if (this.disabled)
                return;
            e.stopPropagation();
            this._open = !this._open;
        };
    }
    connectedCallback() {
        super.connectedCallback();
        document.addEventListener('click', this._onDocumentClick, true);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this._onDocumentClick, true);
    }
    _pick(item) {
        if (item.disabled)
            return;
        this._open = false;
        this.dispatchEvent(new CustomEvent('item-select', {
            detail: { value: item.value, item },
            bubbles: true,
            composed: true
        }));
    }
    _renderEntry(item, index) {
        if (item.header) {
            return html `
        ${index > 0 ? html `<div class="divider"></div>` : ''}
        <div class="menu-header">${item.label}</div>
      `;
        }
        return this._renderItem(item);
    }
    _renderItem(item) {
        const tinted = item.color != null && !item.danger;
        const style = tinted
            ? `--item-icon-color: ${item.color};` +
                `--item-icon-bg: color-mix(in srgb, ${item.color} 14%, white);` +
                `--item-hover-bg: color-mix(in srgb, ${item.color} 12%, white);` +
                `--item-label-color: color-mix(in srgb, ${item.color} 75%, black);`
            : '';
        const classes = `item${item.danger ? ' danger' : ''}${tinted ? ' tinted' : ''}`;
        return html `
      <button
        type="button"
        class=${classes}
        style=${style}
        role="menuitem"
        ?disabled=${item.disabled}
        @click=${() => this._pick(item)}
      >
        ${item.iconName
            ? html `<span class="item-icon"><basic-icon .name=${item.iconName} size="14"></basic-icon></span>`
            : item.icon
                ? html `<span class="item-icon">${unsafeHTML(item.icon)}</span>`
                : ''}
        <span class="item-label">${item.label}</span>
      </button>
    `;
    }
    render() {
        const safe = this.items.filter((i) => !!i);
        const safeItems = safe.filter((i) => !i.danger);
        const dangerItems = safe.filter((i) => i.danger);
        return html `
      <icon-button
        class="trigger"
        .iconName=${this.icon}
        .size=${this.size}
        .variant=${this.variant}
        ?special=${this.special}
        ?outlined=${this.outlined}
        ?dashed=${this.dashed}
        ?disabled=${this.disabled}
        aria-haspopup="menu"
        aria-expanded=${this._open}
        aria-label=${this.ariaLabel || msg('More options', { id: 'kebab.ariaLabel', desc: 'Kebab menu trigger accessible label' })}
        @button-click=${this._toggle}
      ></icon-button>
      <div class="menu ${this._open ? 'open' : ''}" role="menu">
        ${safeItems.map((i, idx) => this._renderEntry(i, idx))}
        ${dangerItems.length > 0 && safeItems.length > 0
            ? html `<div class="divider"></div>`
            : ''}
        ${dangerItems.map((i) => this._renderItem(i))}
      </div>
    `;
    }
};
KebabButton.styles = css `
    :host {
      position: relative;
      display: inline-block;
      font-family: inherit;
    }

    /* The trigger itself is an <icon-button> — its chrome (variant,
       size, special, outlined, dashed) is handled inside that component. */

    .menu {
      position: absolute;
      top: calc(100% + 4px);
      right: 0;
      min-width: 180px;
      padding: 4px;
      background: #fff;
      border: 1px solid #d8e0e8;
      border-radius: 8px;
      box-shadow:
        0 6px 20px rgba(0, 0, 0, 0.12),
        0 2px 4px rgba(0, 0, 0, 0.05);
      z-index: 50;
      display: none;
      flex-direction: column;
      font-size: 12.5px;
    }
    .menu.open {
      display: flex;
    }
    :host([align='left']) .menu {
      right: auto;
      left: 0;
    }

    .item {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: 7px 10px;
      background: transparent;
      border: none;
      border-radius: 5px;
      font: inherit;
      font-size: 12.5px;
      color: var(--item-label-color, #1a2433);
      text-align: left;
      cursor: pointer;
    }
    .item:hover:not([disabled]) {
      background: var(--item-hover-bg, #f4f7fa);
    }
    .item[disabled] {
      cursor: not-allowed;
      opacity: 0.5;
    }
    .item[disabled]:hover {
      background: transparent;
    }
    .item-icon {
      display: inline-flex;
      flex-shrink: 0;
      color: var(--item-icon-color, #6a7585);
    }
    .item-icon svg {
      width: 14px;
      height: 14px;
    }
    /* Tinted-icon variant: rounded chip behind the glyph, used when the
       host passes a color on a menu item. */
    .item.tinted .item-icon {
      width: 24px;
      height: 24px;
      border-radius: 6px;
      align-items: center;
      justify-content: center;
      background: var(--item-icon-bg, transparent);
    }
    .item.danger {
      color: #b53b3b;
    }
    .item.danger:hover {
      background: #fdf6f5;
    }
    .item.danger .item-icon {
      color: #b53b3b;
    }

    .divider {
      height: 1px;
      background: #eef2f6;
      margin: 4px -4px;
    }

    .menu-header {
      padding: 6px 10px 3px;
      font-size: 10.5px;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: #8a95a5;
      user-select: none;
    }
  `;
__decorate([
    property({ type: Array })
], KebabButton.prototype, "items", void 0);
__decorate([
    property({ type: String, attribute: 'aria-label' })
], KebabButton.prototype, "ariaLabel", void 0);
__decorate([
    property({ type: String, reflect: true })
], KebabButton.prototype, "align", void 0);
__decorate([
    property({ type: String })
], KebabButton.prototype, "size", void 0);
__decorate([
    property({ type: String })
], KebabButton.prototype, "variant", void 0);
__decorate([
    property({ type: String })
], KebabButton.prototype, "icon", void 0);
__decorate([
    property({ type: Boolean })
], KebabButton.prototype, "special", void 0);
__decorate([
    property({ type: Boolean })
], KebabButton.prototype, "outlined", void 0);
__decorate([
    property({ type: Boolean })
], KebabButton.prototype, "dashed", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], KebabButton.prototype, "disabled", void 0);
__decorate([
    state()
], KebabButton.prototype, "_open", void 0);
KebabButton = __decorate([
    localized(),
    customElement('kebab-button')
], KebabButton);
export { KebabButton };
//# sourceMappingURL=KebabButton.js.map